import React, { useState, useRef, useEffect } from 'react';
import { 
  Bar, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, Line
} from 'recharts';
import { 
  Send, Bot, User, X, Minimize2, MessageCircle,
  Code, Award, Briefcase, Calendar, Target, Users
} from 'lucide-react';
// Import your API services - adjust the path as needed
import { getProjects, getSkills, getEducation, getCertifications } from '../services/api';

// ============================================
// DYNAMIC CHATBOT - FETCHES DATA FROM API
// ============================================

const AIDashboard = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [chartType, setChartType] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [sangaData, setSangaData] = useState({
    personal: {
      name: 'Sanga Ben',
      role: 'Full-Stack Developer & Designer',
      country: 'Uganda',
      email: 'sangaben07@gmail.com',
      phone: '+256 766 670 007',
    },
    skills: [],
    projects: [],
    education: [],
    certifications: [],
    projectStats: [],
    totalProjects: 0,
    happyClients: 0,
    yearsExperience: 3,
    technologies: 0,
    skillRadar: [],
    achievements: []
  });
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to get project name from various possible field names
  const getProjectName = (project) => {
    return project.name || project.title || project.project_name || project.project_title || project.proj_name || 'Unnamed Project';
  };

  // Helper function to get project description
  const getProjectDescription = (project) => {
    return project.description || project.desc || project.project_description || project.details || 'No description available';
  };

  // Helper function to get project year
  const getProjectYear = (project) => {
    return project.year || project.project_year || (project.created_at ? new Date(project.created_at).getFullYear() : '2024');
  };

  // Helper function to get project tech stack
  const getProjectTech = (project) => {
    return project.tech || project.technologies || project.tech_stack || (project.tech_stack ? project.tech_stack.split(',') : ['Django', 'Python']);
  };

  // ========== FETCH DATA FROM API ==========
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all data from your API
        const [projectsRes, skillsRes, educationRes, certificationsRes] = await Promise.all([
          getProjects().catch(() => ({ data: [] })),
          getSkills().catch(() => ({ data: [] })),
          getEducation().catch(() => ({ data: [] })),
          getCertifications().catch(() => ({ data: [] }))
        ]);
        
        let projects = projectsRes.data || [];
        
        // If projects is not an array, try to extract from response
        if (!Array.isArray(projects) && projects.results) {
          projects = projects.results;
        }
        if (!Array.isArray(projects)) {
          projects = [];
        }
        
        console.log('Fetched projects:', projects);
        
        const skills = skillsRes.data || [];
        const education = educationRes.data || [];
        const certifications = certificationsRes.data || [];
        
        // Calculate statistics from actual data
        const totalProjects = projects.length;
        const happyClients = projects.reduce((sum, p) => sum + (p.clients || p.happy_clients || 0), 0) || 10;
        
        // Create project stats for chart (last 6 months or all projects)
        const projectStats = projects.slice(-6).map((p, index) => ({
          month: p.month || (p.created_at ? new Date(p.created_at).toLocaleString('default', { month: 'short' }) : `Project ${index + 1}`),
          completed: 1,
          satisfaction: p.satisfaction || p.client_satisfaction || 95,
          projectName: getProjectName(p)
        }));
        
        // If no projects, show default stats
        const finalProjectStats = projectStats.length > 0 ? projectStats : [
          { month: 'Jan', completed: 2, satisfaction: 95 },
          { month: 'Feb', completed: 1, satisfaction: 98 },
          { month: 'Mar', completed: 3, satisfaction: 96 },
        ];
        
        // Create skill radar data
        const skillRadar = skills.slice(0, 8).map(s => ({
          skill: s.name || s.skill_name || s.title || 'Unknown',
          value: s.proficiency || s.level || 85,
          fullMark: 100
        }));
        
        // Create achievements from projects
        const achievements = projects.slice(0, 5).map(p => ({
          title: getProjectName(p),
          year: getProjectYear(p),
          description: getProjectDescription(p).substring(0, 100)
        }));
        
        // If no projects, add default achievements
        if (achievements.length === 0) {
          achievements.push(
            { title: 'Graduate - Muni University', year: '2024', description: 'Bachelor of Information Systems' },
            { title: 'Founder - Edurise Initiative', year: '2023', description: 'Empowering students through technology' },
            { title: 'Co-Founder - EcoHope Co Ltd', year: '2024', description: 'Agriculture tech for farmers' }
          );
        }
        
        setSangaData(prev => ({
          ...prev,
          skills: skills,
          projects: projects,
          education: education,
          certifications: certifications,
          projectStats: finalProjectStats,
          totalProjects: totalProjects || 8,
          happyClients: happyClients || 10,
          technologies: skills.length || 7,
          skillRadar: skillRadar,
          achievements: achievements
        }));
        
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // ========== WELCOME MESSAGE ==========
  useEffect(() => {
    if (!loading) {
      const skillNames = sangaData.skills.slice(0, 3).map(s => s.name || s.skill_name || 'Skill').join(', ');
      const projectTitles = sangaData.projects.slice(0, 3).map(p => getProjectName(p)).join(', ');
      
      const welcomeMessage = {
        text: `👋 **Hello!** I'm ${sangaData.personal.name}'s portfolio assistant. I'm here to help you learn about ${sangaData.personal.name} - a ${sangaData.personal.role} from ${sangaData.personal.country}.\n\n**You can ask me about:**\n• His background, education, and certifications\n• His technical skills (${skillNames || 'Django, Python, HTML, CSS, JavaScript'} and more)\n• His projects (${projectTitles || 'Loading projects...'} and others)\n• His leadership roles and achievements\n• How to contact or hire him\n\nWhat would you like to know?`,
        isBot: true,
        quickReplies: [`Who is ${sangaData.personal.name}?`, 'What are his skills?', 'What projects has he built?', 'How to contact him?']
      };
      setMessages([welcomeMessage]);
    }
  }, [loading, sangaData.personal.name, sangaData.personal.role, sangaData.personal.country, sangaData.projects, sangaData.skills]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // ========== SKILL RADAR CHART ==========
  const SkillRadarChart = ({ data }) => (
    <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
      <RadarChart cx="50%" cy="50%" outerRadius="70%" data={data}>
        <PolarGrid stroke="#e5e7eb" strokeDasharray="3 3" />
        <PolarAngleAxis dataKey="skill" tick={{ fill: '#6b7280', fontSize: isMobile ? 9 : 11 }} />
        <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#9ca3af' }} />
        <Radar name="Proficiency" dataKey="value" stroke="#2563eb" strokeWidth={2} fill="#2563eb" fillOpacity={0.3} dot={{ r: 3, fill: '#2563eb' }} />
        <Tooltip content={({ active, payload }) => active && payload && (
          <div style={{ background: 'white', padding: '8px', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
            <p style={{ fontWeight: 'bold', margin: 0, fontSize: '12px' }}>{payload[0]?.payload.skill}</p>
            <p style={{ color: '#2563eb', margin: '5px 0 0', fontSize: '11px' }}>Proficiency: {payload[0]?.value}%</p>
          </div>
        )} />
      </RadarChart>
    </ResponsiveContainer>
  );

  // ========== PERFORMANCE CHART ==========
  const PerformanceComposedChart = ({ data }) => (
    <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
      <ComposedChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="month" stroke="#94a3b8" tick={{ fontSize: isMobile ? 10 : 12 }} />
        <YAxis yAxisId="left" stroke="#10b981" tick={{ fontSize: isMobile ? 10 : 12 }} />
        <YAxis yAxisId="right" orientation="right" stroke="#3b82f6" tick={{ fontSize: isMobile ? 10 : 12 }} />
        <Tooltip 
          content={({ active, payload, label }) => {
            if (active && payload && payload.length) {
              return (
                <div style={{ background: 'white', padding: '8px', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0' }}>
                  <p style={{ fontWeight: 'bold', margin: 0, fontSize: '12px' }}>{payload[0]?.payload.projectName || label}</p>
                  <p style={{ color: '#10b981', margin: '5px 0 0', fontSize: '11px' }}>Projects: {payload[0]?.value}</p>
                  <p style={{ color: '#f59e0b', margin: '2px 0 0', fontSize: '11px' }}>Satisfaction: {payload[1]?.value}%</p>
                </div>
              );
            }
            return null;
          }}
        />
        <Legend wrapperStyle={{ fontSize: isMobile ? '10px' : '12px' }} />
        <Bar yAxisId="left" dataKey="completed" fill="#10b981" name="Projects" radius={[4, 4, 0, 0]} />
        <Line yAxisId="right" type="monotone" dataKey="satisfaction" stroke="#f59e0b" strokeWidth={2} name="Satisfaction %" dot={{ r: 4, fill: '#f59e0b' }} />
      </ComposedChart>
    </ResponsiveContainer>
  );

  // ========== SATISFACTION PIE CHART ==========
  const SatisfactionPieChart = () => {
    const avgSatisfaction = sangaData.projectStats.length > 0 
      ? Math.round(sangaData.projectStats.reduce((sum, p) => sum + p.satisfaction, 0) / sangaData.projectStats.length)
      : 95;
    
    return (
      <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
        <PieChart>
          <Pie data={[
            { name: 'Satisfied', value: avgSatisfaction, color: '#10b981' },
            { name: 'Room for Growth', value: 100 - avgSatisfaction, color: '#f59e0b' }
          ]} cx="50%" cy="50%" innerRadius={isMobile ? 40 : 60} outerRadius={isMobile ? 70 : 90} paddingAngle={3} dataKey="value" label={({ name, percent }) => isMobile ? `${(percent * 100).toFixed(0)}%` : `${name} ${(percent * 100).toFixed(0)}%`}>
            <Cell fill="#10b981" stroke="white" strokeWidth={2} />
            <Cell fill="#f59e0b" stroke="white" strokeWidth={2} />
          </Pie>
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: isMobile ? '10px' : '12px' }} />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  // ========== CHART DISPLAY ==========
  const ChartDisplay = ({ chartType }) => {
    if (!chartType) return null;

    const chartConfigs = {
      skills: { component: SkillRadarChart, data: sangaData.skillRadar, title: '📊 Technical Skills Radar', icon: <Code size={isMobile ? 14 : 18} /> },
      projects: { component: PerformanceComposedChart, data: sangaData.projectStats, title: '📈 Project Performance', icon: <Target size={isMobile ? 14 : 18} /> },
      satisfaction: { component: SatisfactionPieChart, data: null, title: '⭐ Client Satisfaction', icon: <Award size={isMobile ? 14 : 18} /> },
    };

    const config = chartConfigs[chartType];
    if (!config) return null;

    const ChartComponent = config.component;
    
    return (
      <div style={{ marginTop: '12px', padding: isMobile ? '12px' : '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ padding: '4px', background: '#1e3a5f', borderRadius: '8px', color: 'white' }}>
            {config.icon}
          </div>
          <h4 style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '600', margin: 0, color: '#1e293b' }}>{config.title}</h4>
        </div>
        <ChartComponent data={config.data} />
      </div>
    );
  };

  // ========== BOT RESPONSE - DYNAMIC USING API DATA ==========
  const getBotResponse = (message) => {
    const msg = message.toLowerCase();
    
    // Get dynamic data with real project names
    const skillNames = sangaData.skills.slice(0, 5).map(s => s.name || s.skill_name || 'Skill').join(', ');
    
    // Build project list with real names and descriptions
    const projectList = sangaData.projects.map(p => {
      const projectName = getProjectName(p);
      const year = getProjectYear(p);
      const description = getProjectDescription(p).substring(0, 80);
      return `• ${projectName} (${year}) - ${description}`;
    }).join('\n');
    
    const skillList = sangaData.skills.map(s => {
      const skillName = s.name || s.skill_name || 'Unknown Skill';
      const level = s.proficiency || s.level || 85;
      const description = s.description || 'Expertise';
      return `• ${skillName} (${level}%) - ${description}`;
    }).join('\n');
    
    const eduList = sangaData.education.map(e => {
      const degree = e.degree || 'Degree';
      const institution = e.institution || 'University';
      const startYear = e.start_year || 'N/A';
      const endYear = e.end_year || 'Present';
      return `• ${degree} from ${institution} (${startYear} - ${endYear})`;
    }).join('\n');
    
    const certList = sangaData.certifications.map(c => {
      const title = c.title || 'Certification';
      const issuer = c.issuer || 'Provider';
      const year = c.year || 'N/A';
      return `• ${title} - ${issuer} (${year})`;
    }).join('\n');
    
    // ========== ABOUT ==========
    if (msg.includes('who is') || (msg.includes('about') && msg.includes('sanga')) || msg.includes('tell me about')) {
      return {
        text: `**${sangaData.personal.name}** is a ${sangaData.personal.role} from ${sangaData.personal.country}. ${sangaData.education.length > 0 ? `He holds ${sangaData.education[0]?.degree || 'a degree'} from ${sangaData.education[0]?.institution || 'university'}.` : ''} He is the **Founder of Edurise Initiative** and **Co-Founder of EcoHope Co Ltd**. He specializes in full-stack development, graphic design, and web hosting solutions. With ${sangaData.yearsExperience}+ years of experience, he has completed ${sangaData.totalProjects}+ projects with ${sangaData.happyClients}+ happy clients.`,
        quickReplies: ['What are his skills?', 'What projects has he built?', 'How to contact him?']
      };
    }
    
    // ========== PROJECTS - SHOW REAL TITLES ==========
    if (msg.includes('project') || msg.includes('built') || msg.includes('builds') || msg.includes('portfolio') || msg.includes('work') || msg.includes('done')) {
      setChartType('projects');
      const projectCount = sangaData.projects.length;
      const projectTitles = sangaData.projects.map(p => getProjectName(p)).filter(n => n !== 'Unnamed Project').join(', ');
      
      return {
        text: `**Projects Built (${projectCount} Total)**\n\n${projectList || 'No projects added yet. Check back soon!'}\n\n**Project Titles:** ${projectTitles || 'None'}\n\nEach project demonstrates his ability to deliver complete, real-world web solutions.`,
        quickReplies: ['Tell me about my first project', 'Latest project details', 'Project technologies']
      };
    }
    
    // ========== SPECIFIC PROJECT QUERY ==========
    const specificProject = sangaData.projects.find(p => {
      const projectName = getProjectName(p).toLowerCase();
      return msg.includes(projectName) || (projectName && msg.includes(projectName.split(' ')[0]));
    });
    
    if (specificProject && (msg.includes('tell me about') || msg.includes('details') || msg.includes('first project'))) {
      const projectName = getProjectName(specificProject);
      const description = getProjectDescription(specificProject);
      const year = getProjectYear(specificProject);
      const tech = getProjectTech(specificProject);
      
      return {
        text: `**${projectName}**\n\n${description}\n\n**Year:** ${year}\n**Tech Stack:** ${Array.isArray(tech) ? tech.join(', ') : tech}\n**Impact:** Solving real-world problems in Uganda`,
        quickReplies: ['What other projects?', 'His skills', 'Contact him']
      };
    }
    
    // ========== LATEST PROJECT ==========
    if (msg.includes('latest project') || msg.includes('most recent')) {
      const latestProject = sangaData.projects[sangaData.projects.length - 1];
      if (latestProject) {
        const projectName = getProjectName(latestProject);
        const description = getProjectDescription(latestProject);
        const year = getProjectYear(latestProject);
        return {
          text: `**Latest Project: ${projectName}** (${year})\n\n${description}\n\nThis is his most recent work, demonstrating his current skills and expertise.`,
          quickReplies: ['Tell me about other projects', 'His skills', 'Contact him']
        };
      }
    }
    
    // ========== SKILLS ==========
    if (msg.includes('skill') || msg.includes('technology') || msg.includes('tech stack') || msg.includes('knows')) {
      setChartType('skills');
      return {
        text: `**Technical Skills & Expertise**\n\n${skillList || '• Django (92%) - Full-stack web framework\n• Python (90%) - Core programming\n• HTML5 (95%) - Semantic markup\n• CSS3 (88%) - Responsive design\n• JavaScript (85%) - ES6+'}\n\nHe delivers complete end-to-end solutions!`,
        quickReplies: ['Tell me about Django', 'Graphic design skills', 'Web hosting services']
      };
    }
    
    // ========== PROJECT TECHNOLOGIES ==========
    if (msg.includes('project technologies') || (msg.includes('tech') && msg.includes('project'))) {
      const techList = [...new Set(sangaData.projects.flatMap(p => {
        const tech = getProjectTech(p);
        return Array.isArray(tech) ? tech : [tech];
      }))].join(', ');
      
      return {
        text: `**Technologies Used in Projects**\n\n${techList || 'Django, Python, PostgreSQL, HTML, CSS, JavaScript'}\n\nSanga uses modern, industry-standard technologies to build robust web applications.`,
        quickReplies: ['Tell me about his skills', 'Project details', 'Contact him']
      };
    }
    
    // ========== EDUCATION ==========
    if (msg.includes('education') || msg.includes('university') || msg.includes('graduate') || msg.includes('degree') || msg.includes('muni')) {
      return {
        text: `**Education Background**\n\n${eduList || '🎓 Bachelor of Information Systems from Muni University (2020 - 2024)'}\n\nHis academic background provides a strong foundation for building practical, real-world technology solutions.`,
        quickReplies: ['What certifications does he have?', 'What projects has he built?', 'His skills']
      };
    }
    
    // ========== CERTIFICATIONS ==========
    if (msg.includes('certification') || msg.includes('certificate') || msg.includes('courses')) {
      return {
        text: `**Certifications**\n\n${certList || '• Full-Stack Web Development - Meta (2024)\n• Graphic Design Professional - Adobe (2023)\n• Python Programming - University of Michigan (2023)\n• Django Framework Mastery - Coursera (2024)'}\n\nThese certifications demonstrate his commitment to continuous learning.`,
        quickReplies: ['What are his skills?', 'His education', 'His projects']
      };
    }
    
    // ========== LEADERSHIP ==========
    if (msg.includes('leadership') || msg.includes('founder') || msg.includes('entrepreneur')) {
      return {
        text: `**Leadership & Entrepreneurship**\n\n🚀 **Founder - Edurise Initiative** (2023)\n   Educational platform empowering students\n\n🌱 **Co-Founder - EcoHope Co Ltd** (2024)\n   Agriculture technology company\n\nSanga combines technical skills with entrepreneurial vision to build sustainable solutions.`,
        quickReplies: ['Tell me about Edurise', 'Tell me about EcoHope', 'His technical skills']
      };
    }
    
    // ========== CONTACT ==========
    if (msg.includes('contact') || msg.includes('email') || msg.includes('hire') || msg.includes('whatsapp') || msg.includes('phone')) {
      return {
        text: `**Contact ${sangaData.personal.name}**\n\n📧 **Email:** ${sangaData.personal.email}\n💬 **WhatsApp:** ${sangaData.personal.phone}\n💻 **GitHub:** github.com/sangaben\n🔗 **LinkedIn:** linkedin.com/in/ben-sanga\n📍 **Location:** ${sangaData.personal.country}\n\n**Response Time:** Usually within 24 hours\n**Availability:** Open for freelance work and collaborations\n\nReach out today to discuss your project!`,
        quickReplies: ['What services do you offer?', 'What projects has he built?', 'What are his rates?']
      };
    }
    
    // ========== SERVICES ==========
    if (msg.includes('service') || msg.includes('offer') || msg.includes('provide')) {
      return {
        text: `**Services Offered**\n\n💻 **Web Development** - Custom websites, web applications, e-commerce\n🎨 **Graphic Design** - Logo design, branding, marketing materials\n🚀 **Web Hosting** - Domain setup, server deployment, maintenance\n📱 **Technical Consulting** - Project planning, system architecture\n\nContact him for a free consultation!`,
        quickReplies: ['How to contact him?', 'What projects has he built?', 'What are his rates?']
      };
    }
    
    // ========== RATES ==========
    if (msg.includes('price') || msg.includes('cost') || msg.includes('rate') || msg.includes('how much')) {
      return {
        text: `**Pricing & Investment**\n\n💻 **Website Development:** $500 - $3,000\n📱 **Web Application:** $1,000 - $5,000\n🎨 **Graphic Design:** $100 - $500 per project\n🚀 **Web Hosting:** $100/month\n⏱️ **Hourly Rate:** $25/hour\n\n**What's included:**\n✓ Responsive design\n✓ Clean, maintainable code\n✓ 30 days free support\n✓ Source code ownership\n\nContact for a custom quote!`,
        quickReplies: ['Contact him', 'See his work', 'What services do you offer?']
      };
    }
    
    // ========== ACHIEVEMENTS ==========
    if (msg.includes('achievement') || msg.includes('award') || msg.includes('success')) {
      const achievements = sangaData.achievements.map(a => `🏆 **${a.title}** (${a.year})\n   ${a.description}`).join('\n\n');
      return {
        text: `**Key Achievements**\n\n${achievements || '🏆 Graduate - Muni University (2024)\n🏆 Founder - Edurise Initiative (2023)\n🏆 Co-Founder - EcoHope Co Ltd (2024)\n🏆 100% Client Retention'}\n\nThese achievements reflect his commitment to excellence!`,
        quickReplies: ['Tell me about his projects', 'His experience', 'Client satisfaction']
      };
    }
    
    // ========== CLIENT SATISFACTION ==========
    if (msg.includes('satisfaction') || msg.includes('happy') || msg.includes('client')) {
      setChartType('satisfaction');
      const avgSat = sangaData.projectStats.length > 0 
        ? Math.round(sangaData.projectStats.reduce((sum, p) => sum + p.satisfaction, 0) / sangaData.projectStats.length)
        : 96;
      return {
        text: `**Client Satisfaction**\n\n📊 **${avgSat}% average client satisfaction**\n⭐ **92% of clients** rate his work 5 stars\n🔄 **100% client retention**\n👥 **${sangaData.happyClients}+ happy clients**\n\nQuality delivery + Clear communication = Happy clients!`,
        quickReplies: ['What projects has he built?', 'His skills', 'Contact him']
      };
    }
    
    // ========== GREETING ==========
    if (msg.includes('hello') || msg.includes('hi') || msg.includes('hey')) {
      const projectTitles = sangaData.projects.slice(0, 3).map(p => getProjectName(p)).filter(n => n !== 'Unnamed Project').join(', ');
      return {
        text: `👋 **Hello!** I'm ${sangaData.personal.name}'s portfolio assistant. I can tell you about his skills (${skillNames}), projects (${projectTitles || 'various projects'}), education, and how to contact him.\n\nWhat would you like to know?`,
        quickReplies: [`Who is ${sangaData.personal.name}?`, 'What are his skills?', 'What projects has he built?', 'How to contact him?']
      };
    }
    
    // ========== DEFAULT ==========
    const projectTitles = sangaData.projects.slice(0, 5).map(p => getProjectName(p)).filter(n => n !== 'Unnamed Project').join(', ');
    return {
      text: `I don't have that information. I can answer questions about ${sangaData.personal.name} - his skills, projects (${projectTitles || 'various projects'}), education, and how to contact him.\n\n**Try asking:**\n• Who is ${sangaData.personal.name}?\n• What are his skills?\n• What projects has he built?\n• How to contact him?`,
      quickReplies: [`Who is ${sangaData.personal.name}?`, 'What are his skills?', 'What projects has he built?', 'How to contact him?']
    };
  };

  // ========== MESSAGE COMPONENT ==========
  const Message = ({ text, isBot, chartType, quickReplies, onQuickReply }) => {
    const [showQuickReplies, setShowQuickReplies] = useState(true);

    const formatText = (content) => {
      const lines = content.split('\n');
      return lines.map((line, i) => {
        if (line.startsWith('•')) {
          return <div key={i} style={{ marginLeft: isMobile ? '12px' : '16px', marginTop: '4px', color: '#475569', fontSize: isMobile ? '12px' : '13px' }}>{line}</div>;
        }
        if (line.startsWith('✓')) {
          return <div key={i} style={{ marginLeft: isMobile ? '12px' : '16px', marginTop: '2px', color: '#10b981', fontSize: isMobile ? '12px' : '13px' }}>{line}</div>;
        }
        if (line.includes('**') && line.includes('**')) {
          const parts = line.split(/(\*\*[^*]+\*\*)/g);
          return (
            <div key={i}>
              {parts.map((part, j) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                  return <strong key={j} style={{ color: '#1e3a5f' }}>{part.slice(2, -2)}</strong>;
                }
                return <span key={j}>{part}</span>;
              })}
            </div>
          );
        }
        if (line.trim() === '') return <div key={i} style={{ height: '6px' }}></div>;
        return <div key={i} style={{ color: '#334155', fontSize: isMobile ? '12px' : '13px' }}>{line}</div>;
      });
    };

    return (
      <div style={{ display: 'flex', gap: isMobile ? '8px' : '12px', justifyContent: isBot ? 'flex-start' : 'flex-end', animation: 'slideIn 0.3s ease-out' }}>
        <div style={{ display: 'flex', gap: isMobile ? '8px' : '12px', maxWidth: isMobile ? '90%' : '85%', flexDirection: isBot ? 'row' : 'row-reverse' }}>
          <div>
            <div style={{
              width: isMobile ? '30px' : '36px',
              height: isMobile ? '30px' : '36px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: isBot ? '#1e3a5f' : '#10b981',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              {isBot ? <Bot size={isMobile ? 14 : 18} color="white" /> : <User size={isMobile ? 14 : 18} color="white" />}
            </div>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{
              borderRadius: '12px',
              padding: isMobile ? '8px 12px' : '12px 16px',
              background: isBot ? 'white' : '#2563eb',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              border: isBot ? '1px solid #e2e8f0' : 'none'
            }}>
              <div style={{ fontSize: isMobile ? '12px' : '14px', lineHeight: '1.5', whiteSpace: 'pre-wrap', color: isBot ? '#1f2937' : 'white' }}>
                {formatText(text)}
              </div>
            </div>
            
            {chartType && <ChartDisplay chartType={chartType} />}
            
            {isBot && quickReplies && quickReplies.length > 0 && showQuickReplies && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                {quickReplies.map((reply, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setShowQuickReplies(false);
                      onQuickReply(reply);
                    }}
                    style={{
                      padding: isMobile ? '4px 10px' : '6px 12px',
                      fontSize: isMobile ? '10px' : '12px',
                      background: '#f1f5f9',
                      border: '1px solid #e2e8f0',
                      borderRadius: '20px',
                      color: '#475569',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => { e.target.style.background = '#1e3a5f'; e.target.style.color = 'white'; e.target.style.borderColor = '#1e3a5f'; }}
                    onMouseLeave={(e) => { e.target.style.background = '#f1f5f9'; e.target.style.color = '#475569'; e.target.style.borderColor = '#e2e8f0'; }}
                  >
                    {reply}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ========== TYPING INDICATOR ==========
  const TypingIndicator = () => (
    <div style={{ display: 'flex', gap: isMobile ? '8px' : '12px', alignItems: 'flex-start' }}>
      <div style={{ width: isMobile ? '30px' : '36px', height: isMobile ? '30px' : '36px', borderRadius: '50%', background: '#1e3a5f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Bot size={isMobile ? 14 : 18} color="white" />
      </div>
      <div style={{ background: 'white', borderRadius: '12px', padding: isMobile ? '10px 14px' : '12px 16px', border: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', gap: '4px' }}>
          <span style={{ width: '6px', height: '6px', background: '#1e3a5f', borderRadius: '50%', display: 'inline-block', animation: 'bounce 1s infinite' }}></span>
          <span style={{ width: '6px', height: '6px', background: '#1e3a5f', borderRadius: '50%', display: 'inline-block', animation: 'bounce 1s infinite 0.15s' }}></span>
          <span style={{ width: '6px', height: '6px', background: '#1e3a5f', borderRadius: '50%', display: 'inline-block', animation: 'bounce 1s infinite 0.3s' }}></span>
        </div>
      </div>
    </div>
  );

  const handleSend = async (customMessage = null) => {
    const messageToSend = customMessage || input;
    if (!messageToSend.trim()) return;

    const userMessage = { text: messageToSend, isBot: false };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);
    setChartType(null);

    setTimeout(() => {
      const response = getBotResponse(messageToSend);
      const botMessage = { 
        text: response.text, 
        isBot: true, 
        chartType, 
        quickReplies: response.quickReplies 
      };
      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);
    }, 800);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickReply = (reply) => {
    handleSend(reply);
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        position: 'fixed', bottom: '24px', right: '24px', zIndex: 50,
        width: isMobile ? '350px' : '400px', height: isMobile ? '500px' : '500px', background: 'white', borderRadius: '16px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTopColor: '#1e3a5f', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }}></div>
          <p>Loading portfolio data...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Minimized state
  if (isMinimized) {
    return (
      <div style={{
        position: 'fixed', bottom: '20px', right: '20px', zIndex: 50,
        background: 'white', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', padding: isMobile ? '10px 12px' : '12px 16px' }}>
          <div style={{ width: isMobile ? '28px' : '32px', height: isMobile ? '28px' : '32px', borderRadius: '50%', background: '#1e3a5f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Bot size={isMobile ? 12 : 16} color="white" />
          </div>
          <div>
            <h4 style={{ fontWeight: 'bold', margin: 0, fontSize: isMobile ? '11px' : '13px' }}>Sanga Assistant</h4>
            <p style={{ fontSize: isMobile ? '9px' : '11px', color: '#64748b', margin: 0 }}>{sangaData.totalProjects} projects • Ready</p>
          </div>
          <button onClick={() => setIsMinimized(false)} style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer' }}>
            <MessageCircle size={isMobile ? 12 : 14} color="#64748b" />
          </button>
        </div>
      </div>
    );
  }

  // Closed state (floating button)
  if (!isOpen) {
    return (
      <button onClick={() => setIsOpen(true)} style={{
        position: 'fixed', bottom: '20px', right: '20px', zIndex: 50,
        cursor: 'pointer', background: 'none', border: 'none'
      }}>
        <div style={{ position: 'relative' }}>
          <div style={{
            position: 'absolute', inset: 0, background: '#1e3a5f',
            borderRadius: '50%', filter: 'blur(12px)', opacity: 0.5
          }}></div>
          <div style={{
            position: 'relative', background: '#1e3a5f', color: 'white',
            padding: isMobile ? '12px' : '14px', borderRadius: '50%', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)'
          }}>
            <MessageCircle size={isMobile ? 18 : 22} />
          </div>
        </div>
      </button>
    );
  }

  // Main dashboard - Responsive width and height
  return (
    <div style={{
      position: 'fixed', bottom: isMobile ? '10px' : '24px', right: isMobile ? '10px' : '24px', zIndex: 50,
      width: isMobile ? 'calc(100vw - 20px)' : '500px',
      maxWidth: isMobile ? '450px' : '500px',
      height: isMobile ? 'calc(100vh - 20px)' : '650px',
      maxHeight: isMobile ? '700px' : '650px',
      background: 'white', borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      border: '1px solid #e2e8f0'
    }}>
      <style>
        {`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-2px); }
          }
          .overflow-y-auto::-webkit-scrollbar { width: 4px; }
          .overflow-y-auto::-webkit-scrollbar-track { background: #f1f5f9; }
          .overflow-y-auto::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
          
          @media (max-width: 480px) {
            .overflow-y-auto::-webkit-scrollbar { width: 2px; }
          }
        `}
      </style>
      
      {/* Header - Responsive */}
      <div style={{ background: '#1e3a5f', color: 'white', padding: isMobile ? '12px 16px' : '16px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
            <div style={{ padding: isMobile ? '6px' : '8px', background: 'rgba(255,255,255,0.15)', borderRadius: '10px' }}>
              <Code size={isMobile ? 14 : 18} />
            </div>
            <div>
              <h3 style={{ fontWeight: 'bold', fontSize: isMobile ? '14px' : '16px', margin: 0 }}>{sangaData.personal.name} Assistant</h3>
              <p style={{ fontSize: isMobile ? '9px' : '11px', margin: '2px 0 0', opacity: 0.8 }}>{sangaData.personal.role} • {sangaData.personal.country}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: isMobile ? '4px' : '8px' }}>
            <button onClick={() => setIsMinimized(true)} style={{ padding: '4px', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '6px', cursor: 'pointer', color: 'white' }}>
              <Minimize2 size={isMobile ? 12 : 14} />
            </button>
            <button onClick={() => setIsOpen(false)} style={{ padding: '4px', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '6px', cursor: 'pointer', color: 'white' }}>
              <X size={isMobile ? 12 : 14} />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Bar - Responsive Grid */}
      <div style={{ background: '#f8fafc', padding: isMobile ? '8px 12px' : '10px 16px', borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: isMobile ? '4px' : '8px', textAlign: 'center' }}>
          {[
            { icon: <Briefcase size={isMobile ? 10 : 14} />, label: 'Projects', value: sangaData.totalProjects, color: '#2563eb' },
            { icon: <Users size={isMobile ? 10 : 14} />, label: 'Clients', value: sangaData.happyClients, color: '#10b981' },
            { icon: <Calendar size={isMobile ? 10 : 14} />, label: 'Experience', value: `${sangaData.yearsExperience}y`, color: '#f59e0b' },
            { icon: <Code size={isMobile ? 10 : 14} />, label: 'Skills', value: sangaData.technologies, color: '#8b5cf6' }
          ].map((stat, idx) => (
            <div key={idx} style={{ padding: isMobile ? '4px' : '6px', borderRadius: '8px' }}>
              <div style={{ color: stat.color, marginBottom: '2px', display: 'flex', justifyContent: 'center' }}>{stat.icon}</div>
              <p style={{ fontSize: isMobile ? '8px' : '10px', color: '#64748b', margin: 0 }}>{stat.label}</p>
              <p style={{ fontWeight: 'bold', fontSize: isMobile ? '11px' : '13px', margin: 0, color: '#1e293b' }}>{stat.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Messages Area */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: isMobile ? '12px' : '16px', display: 'flex',
        flexDirection: 'column', gap: isMobile ? '10px' : '12px', background: '#ffffff'
      }} className="overflow-y-auto">
        {messages.map((msg, idx) => (
          <Message 
            key={idx} text={msg.text} isBot={msg.isBot}
            chartType={msg.chartType} quickReplies={msg.quickReplies}
            onQuickReply={handleQuickReply}
          />
        ))}
        {isTyping && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area - Responsive */}
      <div style={{ padding: isMobile ? '10px 12px' : '12px 16px', borderTop: '1px solid #e2e8f0', background: 'white' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about skills, projects..."
            style={{
              flex: 1, padding: isMobile ? '8px' : '10px', border: '1px solid #e2e8f0', borderRadius: '12px',
              resize: 'none', background: '#f8fafc', fontSize: isMobile ? '12px' : '13px', fontFamily: 'inherit',
              outline: 'none'
            }}
            rows="1"
          />
          <button onClick={() => handleSend()} disabled={!input.trim()} style={{
            padding: isMobile ? '0 12px' : '0 16px', background: !input.trim() ? '#cbd5e1' : '#1e3a5f',
            color: 'white', border: 'none', borderRadius: '12px',
            cursor: !input.trim() ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', gap: '4px', fontSize: isMobile ? '11px' : '13px', fontWeight: '500'
          }}>
            <Send size={isMobile ? 12 : 14} />
            <span>{isMobile ? '' : 'Send'}</span>
          </button>
        </div>
        <p style={{ fontSize: isMobile ? '8px' : '10px', textAlign: 'center', margin: '8px 0 0', color: '#94a3b8' }}>
          {sangaData.projects.length} projects • {sangaData.skills.length} skills
        </p>
      </div>
    </div>
  );
};

export default AIDashboard;